import { useLocation } from 'wouter';
import { useAuth } from './useAuth';
import { useParams } from 'wouter';
import { parseAdminTournamentIdFromPath } from '@shared/tournamentUrls';

export type NavContext = 'public' | 'tournament-public' | 'admin' | 'coach' | 'none';

export interface NavContextData {
  context: NavContext;
  tournamentId: string | null;
  organizationId: string | null;
  coachTeamToken: string | null;
  isAuthenticated: boolean;
}

/**
 * Determines the navigation context based on the current route and user authentication
 */
export function useNavContext(): NavContextData {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const params = useParams();

  // League Management routes - NO tournament context
  // Must check this FIRST before extracting tournamentId
  const isLeagueRoute = location.startsWith('/admin/league') || location.startsWith('/fit/p/');

  // Extract IDs from route params
  // Don't extract tournamentId from league routes (they have :teamSlug, not :tournamentId)
  let tournamentId = isLeagueRoute ? null : params.tournamentId || null;
  let organizationId = params.orgId || null;
  let coachTeamToken = (params as any).teamHash || null;

  // If tournamentId not in params, try to extract from URL path (but not league routes)
  if (!isLeagueRoute && !tournamentId && location.match(/^\/tournament\/([^/?]+)/)) {
    const match = location.match(/^\/tournament\/([^/?]+)/);
    if (match) {
      tournamentId = match[1];
    }
  }

  // Coach routes:
  //   /coach/:teamHash                 -> teamHash is the team management token
  //   /coach/league/:token             -> nested league kiosk; the actual coach
  //                                       token is the SECOND segment, not the
  //                                       literal "league". Without this guard
  //                                       UnifiedMobileNav's "My Team" link
  //                                       points at /coach/league (404).
  if (!coachTeamToken) {
    const coachLeagueMatch = location.match(/^\/coach\/league\/([^/?]+)/);
    if (coachLeagueMatch) {
      coachTeamToken = coachLeagueMatch[1];
    } else {
      const coachMatch = location.match(/^\/coach\/(?!league(?:\/|$))([^/?]+)/);
      if (coachMatch) {
        coachTeamToken = coachMatch[1];
      }
    }
  }

  // If organizationId not in params, try to extract from URL path
  if (!organizationId && location.match(/^\/org\/([^/?]+)/)) {
    const match = location.match(/^\/org\/([^/?]+)/);
    if (match) {
      organizationId = match[1];
    }
  }

  if (!organizationId && location.match(/^\/admin\/org\/([^/?]+)/)) {
    const match = location.match(/^\/admin\/org\/([^/?]+)/);
    if (match) {
      organizationId = match[1];
    }
  }

  // Also extract tournamentId from admin routes (but not league routes)
  if (!isLeagueRoute && !tournamentId) {
    const adminTournamentId = parseAdminTournamentIdFromPath(location);
    if (adminTournamentId) {
      tournamentId = adminTournamentId;
    } else if (location.match(/\/tournament\/([^/?]+)/)) {
      const match = location.match(/\/tournament\/([^/?]+)/);
      if (match) {
        tournamentId = match[1];
      }
    }
  }

  // Determine navigation context
  let context: NavContext = 'none';

  // Hide nav on auth/onboarding pages
  if (
    ['/login', '/register', '/welcome', '/onboarding', '/forgot-password', '/reset-password'].some(
      path => location.startsWith(path)
    )
  ) {
    return {
      context: 'none',
      tournamentId: null,
      organizationId: null,
      coachTeamToken: null,
      isAuthenticated: false,
    };
  }

  // League Management — LeagueAdminShell owns chrome; hide tournament UnifiedMobileNav
  if (location.startsWith('/admin/league')) {
    context = 'none';
    organizationId = '7940b20e-9d00-4728-b40f-335139f8f3b2';
  }
  // Fit form routes (player /fit/p/* and coach /fit/c/*) — single-purpose
  // magic-link forms. No global chrome to keep the form focused.
  else if (location.startsWith('/fit/')) {
    context = 'none';
  }
  // Admin routes - org-scoped tournament management
  else if (location.match(/^\/org\/[^/]+\/tournaments/)) {
    context = 'admin';
  }
  // Legacy admin shortcuts
  else if (
    location.startsWith('/admin-portal') ||
    location.match(/^\/admin\/org\/[^/]+/) ||
    location.match(
      /^\/admin\/(?!league(?:\/|$)|org(?:\/|$)|tournaments(?:\/|$)|booking(?:\/|$))[^/?]+/
    )
  ) {
    context = 'admin';
  }
  // Other admin routes (settings, admin portal, etc.)
  else if (location.match(/^\/org\/[^/]+\/(admin|settings|communications|weather|teams|reports)/)) {
    context = 'admin';
  }
  // Coach operational route
  else if (location.match(/^\/coach\/[^/]+/)) {
    context = 'coach';
  }
  // Tournament public routes
  else if (location.match(/^\/tournament\/[^/]+/)) {
    context = 'tournament-public';
  }
  // Public routes (directory, home, org pages)
  else if (
    location === '/' ||
    location.startsWith('/directory') ||
    location.startsWith('/dashboard/')
  ) {
    context = 'public';
  }
  // Org pages that aren't admin routes - show public nav
  else if (location.startsWith('/org/')) {
    context = 'public';
  }
  // Fallback: if we have a tournamentId in params, assume tournament context
  else if (tournamentId) {
    context = 'tournament-public';
  }
  // Final fallback: show public nav for any other route (except auth pages)
  else {
    context = 'public';
  }

  return {
    context,
    tournamentId,
    organizationId,
    coachTeamToken,
    isAuthenticated,
  };
}
